import React, { useEffect, useState, useCallback } from 'react';
import { Mail, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';

interface InvitationMetrics {
  sent: number;
  accepted: number;
  declined: number;
  pending: number;
  expired: number;
  revoked: number;
  bounced: number;
  delivered: number;
  public_paper_requests: number;
  private_paper_requests: number;
  papers_with_human_reviews_public: number;
  papers_with_human_reviews_private: number;
  acceptance_rate: number;
}

const InvitationMetricsCard: React.FC = () => {
  const [windowDays, setWindowDays] = useState<number>(30);
  const [metrics, setMetrics] = useState<InvitationMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_review_invitation_metrics', {
      window_days: windowDays,
    });
    if (error) {
      console.error('Failed to load invitation metrics:', error);
      setMetrics(null);
    } else {
      // Function returns SETOF (one row) — supabase gives us an array
      const row = Array.isArray(data) ? data[0] : (data as InvitationMetrics | null);
      setMetrics(row ?? null);
    }
    setLoading(false);
  }, [windowDays]);

  useEffect(() => {
    load();
  }, [load]);

  const isEmpty = !metrics || metrics.sent === 0;
  const acceptancePct = metrics?.acceptance_rate
    ? Math.round(metrics.acceptance_rate * 100)
    : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div className="flex items-start gap-2">
          <Mail className="h-5 w-5 mt-0.5 text-muted-foreground" aria-hidden />
          <div>
            <CardTitle className="text-base">Reviewer Invitations</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Invitations sent to peers by paper authors
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
            <SelectTrigger className="h-8 w-[120px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="0">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={load} disabled={loading} className="h-8 w-8">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : isEmpty ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No invitations sent in this window yet.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Metric label="Sent" value={metrics!.sent} />
              <Metric label="Accepted" value={metrics!.accepted} accent="text-green-600 dark:text-green-400" />
              <Metric label="Declined" value={metrics!.declined} />
              <Metric label="Acceptance rate" value={`${acceptancePct}%`} />
              <Metric label="Pending" value={metrics!.pending} />
              <Metric label="Delivered" value={metrics!.delivered} />
              <Metric label="Bounced" value={metrics!.bounced} accent={metrics!.bounced > 0 ? 'text-destructive' : undefined} />
              <Metric label="Expired / revoked" value={metrics!.expired + metrics!.revoked} />
            </div>

            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Public papers</p>
                <p className="font-medium">
                  {metrics!.public_paper_requests} requests · {metrics!.papers_with_human_reviews_public} with human reviews
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Private papers</p>
                <p className="font-medium">
                  {metrics!.private_paper_requests} requests · {metrics!.papers_with_human_reviews_private} with human reviews
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Metric: React.FC<{ label: string; value: number | string; accent?: string }> = ({
  label,
  value,
  accent,
}) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl font-semibold ${accent ?? ''}`}>{value}</p>
  </div>
);

export default InvitationMetricsCard;
